import axios from 'axios';
import { captureMessage } from '@sentry/browser';
import Constants from 'controllers/constants';
import { User } from 'types';

export const isHistoryGrantId = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/.test(value);

export async function revokeLegacyHistoryGrant(
  sessionId: string,
): Promise<void> {
  await axios.delete(
    `${Constants.internalApiHost}/v2/shops/history-handoff/session/${sessionId}`,
    { withCredentials: true, timeout: 15_000 },
  );
}

interface HistoryTrial {
  active?: boolean;
  days_left?: number;
  end_date?: string | Date | null;
}

export interface HistoryUser extends User {
  free_trial?: HistoryTrial;
  shop_statistics?: unknown;
}

export async function loadLegacyHistoryUser(
  token: string,
): Promise<HistoryUser> {
  const { data } = await axios.get<{ data: HistoryUser }>(
    `${Constants.internalApiHost}/me`,
    {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 15_000,
    },
  );
  if (!data.data) throw new Error('Could not load Postscript 1.0 history.');
  const user = data.data;
  return {
    ...user,
    short_link_domain: user.short_link_domain || 'pscr.pt',
    shop_statistics: null,
    ...(user.free_trial
      ? {
          free_trial: {
            ...user.free_trial,
            end_date: user.free_trial.end_date
              ? new Date(user.free_trial.end_date)
              : null,
          },
        }
      : {}),
  };
}

export async function discardLegacyHistoryGrant(
  sessionId: string,
): Promise<void> {
  try {
    await revokeLegacyHistoryGrant(sessionId);
  } catch {
    try {
      captureMessage(
        'Could not discard an unfinished Postscript 1.0 history session',
        'warning',
      );
    } catch {
      /* Cleanup reporting is best effort. */
    }
  }
}
